import type { AdminCampaignSummary } from "~vue/api/generated/reflections-admin";
import { DateTime, type DurationLike } from "luxon";

// The select's "no filter" value: an empty string is reserved by the select primitive.
export const ALL_TEMPLATES = "all";
export const CUSTOM_RANGE = "custom";

// Keep the keys in display order: the select renders them as given.
export const PREVIOUS_CYCLES_RANGE_KEYS = ["all", "last_30_days", "last_90_days", "last_6_months", "last_12_months", CUSTOM_RANGE] as const;
export type PreviousCyclesRangeKey = (typeof PREVIOUS_CYCLES_RANGE_KEYS)[number];

const PRESET_RANGE_DURATIONS: Partial<Record<PreviousCyclesRangeKey, DurationLike>> = {
    last_30_days: { days: 30 },
    last_90_days: { days: 90 },
    last_6_months: { months: 6 },
    last_12_months: { months: 12 },
};

// yyyy-MM-dd bounds, each optional: an open side is unbounded.
export interface ClosedDateRange {
    startDate: string | null;
    endDate: string | null;
}

// An admin can close a campaign before its scheduled date, so the actual close wins
// over the scheduled one. updated_at only stands in for rows closed before closed_at existed.
export function effectiveClosedAt(campaign: AdminCampaignSummary): string | null {
    return campaign.closed_at ?? campaign.closes_at ?? campaign.updated_at ?? null;
}

function closedAtMillis(campaign: AdminCampaignSummary): number | null {
    const iso = effectiveClosedAt(campaign);
    if (!iso) {
        return null;
    }
    const at = DateTime.fromISO(iso);
    return at.isValid ? at.toMillis() : null;
}

// Newest close first; a campaign without any date goes last.
export function sortClosedCampaignsNewestFirst(campaigns: AdminCampaignSummary[]): AdminCampaignSummary[] {
    const millis = (campaign: AdminCampaignSummary) => closedAtMillis(campaign) ?? Number.NEGATIVE_INFINITY;
    return [...campaigns].sort((a, b) => millis(b) - millis(a));
}

// One option per template that appears among the campaigns, named alphabetically so
// the select reads predictably; a template retired since its campaign stays findable.
export function closedCampaignTemplates(campaigns: AdminCampaignSummary[]): { key: string; label: string }[] {
    const names = new Map<string, string>();
    for (const campaign of campaigns) {
        names.set(campaign.template.id, campaign.template.name);
    }
    return [...names.entries()].map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label));
}

// A preset is a start bound only: everything it admits runs to today.
export function presetRangeStart(rangeKey: PreviousCyclesRangeKey, now: DateTime = DateTime.local()): string | null {
    const duration = PRESET_RANGE_DURATIONS[rangeKey];
    return duration ? now.minus(duration).toISODate() : null;
}

// A campaign belongs to a range when the date it closed falls inside it, the same
// date the list sorts by. An undated campaign can only claim the unbounded range.
export function campaignClosedInRange(campaign: AdminCampaignSummary, range: ClosedDateRange): boolean {
    if (range.startDate === null && range.endDate === null) {
        return true;
    }
    const millis = closedAtMillis(campaign);
    if (millis === null) {
        return false;
    }
    const closed = DateTime.fromMillis(millis);
    if (range.startDate !== null) {
        const start = DateTime.fromISO(range.startDate);
        if (start.isValid && closed < start.startOf("day")) {
            return false;
        }
    }
    if (range.endDate !== null) {
        const end = DateTime.fromISO(range.endDate);
        if (end.isValid && closed > end.endOf("day")) {
            return false;
        }
    }
    return true;
}
